import { useCallback, useEffect, useRef, useState } from "react";

import {
  createHybridInvite,
  listHybridInvites,
  listHybridShares,
  revokeHybridInvite,
  revokeHybridShare,
  searchKindrawUsers,
  updateHybridShareRole,
} from "./api";
import { KindrawIcon } from "./icons";
import { userHandle, userSubtitle } from "./identity";
import { PublicShareLinkSection } from "./PublicShareLinkSection";
import { CreatedInviteBox, SharePendingInvites } from "./SharePendingInvites";
import { useKindrawI18n } from "./i18n";
import { getErrorMessage } from "./utils";

import type { ChangeEvent } from "react";

import type {
  KindrawCreatedInvite,
  KindrawHybridShare,
  KindrawPendingInvite,
  KindrawShareLink,
  KindrawShareLinkAccess,
  KindrawShareRole,
  KindrawUser,
} from "./types";

const SEARCH_DEBOUNCE_MS = 250;

const KindrawPersonAvatar = ({ user }: { user: KindrawUser }) =>
  user.avatarUrl ? (
    <img alt="" className="kindraw-sharemodal__avatar" src={user.avatarUrl} />
  ) : (
    <span
      aria-hidden="true"
      className="kindraw-sharemodal__avatar kindraw-sharemodal__avatar--fallback"
    >
      {(user.name || userHandle(user)).charAt(0).toUpperCase()}
    </span>
  );

export const ShareHybridModal = ({
  hybrid,
  onClose,
  onChange,
  shareLinks,
  onCreateShareLink,
  onRevokeShareLink,
  buildShareUrl,
  busy,
}: {
  hybrid: { id: string; title: string };
  onClose: () => void;
  /** Disparado após qualquer mutação bem-sucedida (ex.: refreshTree). */
  onChange?: () => void;
  // Link público (mesma seção do popover de Drawing/Doc, embutida aqui).
  shareLinks: KindrawShareLink[];
  onCreateShareLink: (access?: KindrawShareLinkAccess) => Promise<void> | void;
  onRevokeShareLink: (shareLinkId: string) => Promise<void> | void;
  buildShareUrl?: (token: string) => string;
  busy?: boolean;
}) => {
  const { t } = useKindrawI18n();
  const [shares, setShares] = useState<KindrawHybridShare[] | null>(null);
  const [invites, setInvites] = useState<KindrawPendingInvite[]>([]);
  const [createdInvite, setCreatedInvite] =
    useState<KindrawCreatedInvite | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<KindrawUser[]>([]);
  const [searching, setSearching] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<KindrawUser | null>(null);
  const [role, setRole] = useState<KindrawShareRole>("editor");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);
  const [busyShareId, setBusyShareId] = useState<string | null>(null);
  const [busyInviteId, setBusyInviteId] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const inviteRef = useRef<HTMLDivElement>(null);

  const loadShares = useCallback(async () => {
    setListError(null);
    try {
      const [sharesResponse, invitesResponse] = await Promise.all([
        listHybridShares(hybrid.id),
        listHybridInvites(hybrid.id),
      ]);
      setShares(sharesResponse.shares);
      setInvites(invitesResponse.invites);
    } catch (error) {
      setListError(
        getErrorMessage(error, t("kindraw.hybrid.loadAccessFailed")),
      );
      setShares([]);
    }
  }, [hybrid.id, t]);

  useEffect(() => {
    void loadShares();
  }, [loadShares]);

  useEffect(() => {
    inputRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  useEffect(() => {
    if (!resultsOpen) {
      return;
    }
    const onPointerDown = (event: PointerEvent) => {
      if (
        inviteRef.current &&
        !inviteRef.current.contains(event.target as Node)
      ) {
        setResultsOpen(false);
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [resultsOpen]);

  useEffect(() => {
    const trimmed = query.trim().replace(/^@/, "");
    if (selectedUser || trimmed.length === 0) {
      setResults([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      void searchKindrawUsers(trimmed)
        .then((response) => {
          if (cancelled) {
            return;
          }
          setResults(response.users);
          setResultsOpen(true);
        })
        .catch(() => {
          if (!cancelled) {
            setResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) {
            setSearching(false);
          }
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [query, selectedUser]);

  const existingLogins = new Set(
    (shares || []).map((share) => userHandle(share.user).toLowerCase()),
  );

  const handleSelectUser = useCallback((user: KindrawUser) => {
    setSelectedUser(user);
    setQuery(userHandle(user));
    setResultsOpen(false);
    setInviteError(null);
  }, []);

  const handleQueryChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
      setSelectedUser(null);
      setInviteError(null);
    },
    [],
  );

  // Sempre gera um LINK de convite (fluxo único), mesmo selecionando um usuário
  // existente. O e-mail/identificador é só informativo; o link é a credencial.
  const handleInvite = useCallback(async () => {
    const identifier = (
      (selectedUser ? selectedUser.email || userHandle(selectedUser) : "") ||
      query.trim().replace(/^@/, "")
    ).trim();
    if (!identifier) {
      return;
    }
    setInviting(true);
    setInviteError(null);
    try {
      const response = await createHybridInvite(hybrid.id, identifier, role);
      setCreatedInvite(response.invite);
      setQuery("");
      setSelectedUser(null);
      setResults([]);
      setResultsOpen(false);
      await loadShares();
      onChange?.();
    } catch (error) {
      setInviteError(getErrorMessage(error, t("kindraw.hybrid.inviteFailed")));
    } finally {
      setInviting(false);
    }
  }, [selectedUser, query, hybrid.id, role, loadShares, onChange, t]);

  const handleRevokeInvite = useCallback(
    async (invite: KindrawPendingInvite) => {
      setBusyInviteId(invite.id);
      setListError(null);
      try {
        await revokeHybridInvite(hybrid.id, invite.id);
        if (createdInvite?.id === invite.id) {
          setCreatedInvite(null);
        }
        await loadShares();
        onChange?.();
      } catch (error) {
        setListError(
          getErrorMessage(error, t("kindraw.hybrid.cancelInviteFailed")),
        );
      } finally {
        setBusyInviteId(null);
      }
    },
    [hybrid.id, createdInvite, loadShares, onChange, t],
  );

  const handleRoleChange = useCallback(
    async (share: KindrawHybridShare, nextRole: KindrawShareRole) => {
      if (nextRole === share.role) {
        return;
      }
      setBusyShareId(share.id);
      setListError(null);
      try {
        await updateHybridShareRole(hybrid.id, share.id, nextRole);
        await loadShares();
        onChange?.();
      } catch (error) {
        setListError(
          getErrorMessage(error, t("kindraw.hybrid.changeRoleFailed")),
        );
      } finally {
        setBusyShareId(null);
      }
    },
    [hybrid.id, loadShares, onChange, t],
  );

  const handleRevoke = useCallback(
    async (share: KindrawHybridShare) => {
      setBusyShareId(share.id);
      setListError(null);
      try {
        await revokeHybridShare(hybrid.id, share.id);
        await loadShares();
        onChange?.();
      } catch (error) {
        setListError(
          getErrorMessage(error, t("kindraw.hybrid.removeAccessFailed")),
        );
      } finally {
        setBusyShareId(null);
      }
    },
    [hybrid.id, loadShares, onChange, t],
  );

  const canInvite = !inviting && query.trim().replace(/^@/, "").length > 0;

  return (
    <div
      className="kindraw-modal-overlay"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        aria-labelledby="kindraw-sharehybrid-title"
        aria-modal="true"
        className="kindraw-modal kindraw-sharemodal"
        role="dialog"
      >
        <div className="kindraw-sharemodal__head">
          <h2 id="kindraw-sharehybrid-title">
            {t("kindraw.hybrid.title", { title: hybrid.title })}
          </h2>
          <button
            aria-label={t("kindraw.hybrid.close")}
            className="kindraw-sharemodal__close"
            onClick={onClose}
            type="button"
          >
            <KindrawIcon name="close" size={16} />
          </button>
        </div>

        <p className="kindraw-sharemodal__hint">
          {t("kindraw.hybrid.hint")}
        </p>

        {/* Acesso geral: link público + permissão (read / editar ao vivo) */}
        <div className="kindraw-sharemodal__section">
          <span className="kindraw-sharemodal__section-label">
            {t("kindraw.hybrid.linkAccessLabel")}
          </span>
          <PublicShareLinkSection
            buildShareUrl={buildShareUrl}
            busy={busy}
            onCreateShareLink={onCreateShareLink}
            onRevokeShareLink={onRevokeShareLink}
            shareLinks={shareLinks}
            supportsLiveEdit
          />
        </div>

        <div className="kindraw-sharemodal__invite" ref={inviteRef}>
          <div className="kindraw-sharemodal__invite-row">
            <div className="kindraw-sharemodal__field">
              <input
                aria-label={t("kindraw.hybrid.inviteInputAria")}
                autoComplete="off"
                className="kindraw-sharemodal__input"
                onChange={handleQueryChange}
                onFocus={() => {
                  if (results.length) {
                    setResultsOpen(true);
                  }
                }}
                placeholder={t("kindraw.hybrid.invitePlaceholder")}
                ref={inputRef}
                type="text"
                value={query}
              />
            </div>
            <select
              aria-label={t("kindraw.hybrid.inviteRoleAria")}
              className="kindraw-sharemodal__roleselect"
              onChange={(event) =>
                setRole(event.target.value as KindrawShareRole)
              }
              value={role}
            >
              <option value="editor">{t("kindraw.hybrid.roleEditor")}</option>
              <option value="viewer">{t("kindraw.hybrid.roleViewer")}</option>
            </select>
            <button
              className="kindraw-btn kindraw-btn--primary kindraw-btn--sm"
              disabled={!canInvite}
              onClick={() => void handleInvite()}
              type="button"
            >
              {inviting
                ? t("kindraw.hybrid.inviting")
                : t("kindraw.hybrid.invite")}
            </button>
          </div>

          <p className="kindraw-sharemodal__invite-help">
            {t("kindraw.hybrid.inviteHelp")}
          </p>

          {resultsOpen && (results.length > 0 || searching) ? (
            <ul className="kindraw-sharemodal__results" role="listbox">
              {searching && results.length === 0 ? (
                <li className="kindraw-sharemodal__result-empty">
                  {t("kindraw.hybrid.searching")}
                </li>
              ) : null}
              {results.map((user) => {
                const already = existingLogins.has(
                  userHandle(user).toLowerCase(),
                );
                return (
                  <li key={user.id}>
                    <button
                      className="kindraw-sharemodal__result"
                      disabled={already}
                      onClick={() => handleSelectUser(user)}
                      role="option"
                      aria-selected={false}
                      type="button"
                    >
                      <KindrawPersonAvatar user={user} />
                      <span className="kindraw-sharemodal__result-text">
                        <strong>{user.name || userHandle(user)}</strong>
                        <span>{userSubtitle(user)}</span>
                      </span>
                      {already ? (
                        <span className="kindraw-sharemodal__result-tag">
                          {t("kindraw.hybrid.alreadyHasAccess")}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {inviteError ? (
            <p className="kindraw-sharemodal__error">{inviteError}</p>
          ) : null}
        </div>

        {createdInvite ? <CreatedInviteBox invite={createdInvite} /> : null}

        <div className="kindraw-sharemodal__people">
          <span className="kindraw-sharemodal__people-label">
            {t("kindraw.hybrid.peopleLabel")}
          </span>
          {shares === null ? (
            <p className="kindraw-sharemodal__hint">
              {t("kindraw.hybrid.loading")}
            </p>
          ) : shares.length === 0 && invites.length === 0 ? (
            <p className="kindraw-sharemodal__hint">
              {t("kindraw.hybrid.noPeople")}
            </p>
          ) : (
            <ul className="kindraw-sharemodal__list">
              <SharePendingInvites
                busyInviteId={busyInviteId}
                invites={invites}
                onRevoke={(invite) => void handleRevokeInvite(invite)}
              />
              {shares.map((share) => {
                const busy = busyShareId === share.id;
                return (
                  <li className="kindraw-sharemodal__person" key={share.id}>
                    <KindrawPersonAvatar user={share.user} />
                    <span className="kindraw-sharemodal__person-text">
                      <strong>
                        {share.user.name || userHandle(share.user)}
                      </strong>
                      <span>{userSubtitle(share.user)}</span>
                    </span>
                    <select
                      aria-label={t("kindraw.hybrid.roleForUser", {
                        handle: userHandle(share.user),
                      })}
                      className="kindraw-sharemodal__roleselect kindraw-sharemodal__roleselect--inline"
                      disabled={busy}
                      onChange={(event) =>
                        void handleRoleChange(
                          share,
                          event.target.value as KindrawShareRole,
                        )
                      }
                      value={share.role}
                    >
                      <option value="editor">
                        {t("kindraw.hybrid.roleEditor")}
                      </option>
                      <option value="viewer">
                        {t("kindraw.hybrid.roleViewer")}
                      </option>
                    </select>
                    <button
                      aria-label={t("kindraw.hybrid.removeUser", {
                        handle: userHandle(share.user),
                      })}
                      className="kindraw-sharemodal__remove"
                      disabled={busy}
                      onClick={() => void handleRevoke(share)}
                      type="button"
                    >
                      <KindrawIcon name="trash" size={15} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {listError ? (
            <p className="kindraw-sharemodal__error">{listError}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};
